import type { QueryClient } from '@tanstack/react-query';

export const userProfileKey = (userId: string | undefined) => ['userProfile', userId] as const;

export const invalidateUserProfile = (queryClient: QueryClient, userId: string) =>
  queryClient.invalidateQueries({ queryKey: userProfileKey(userId) });

export const currentUserKey = () => ['currentUser'] as const;

export const invalidateCurrentUser = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: currentUserKey() });

/** Shared root of the users list/search families. The `user.updated` SSE
 *  handler sweeps this as a prefix to refresh both `usersListKey` and
 *  `usersSearchKey` on an identity change (a rename or an erasure). */
export const usersBaseKey = () => ['users'] as const;

export interface UsersListParams {
  skip?: number;
  limit?: number;
}

export const usersListKey = (params?: UsersListParams) => [...usersBaseKey(), params] as const;

export interface UsersSearchKeyParams {
  search: string;
  excludeCurrentUser: boolean;
}

// Infinite user search/autocomplete (`useInfiniteUsers`). Shares the `['users']`
// root (`usersBaseKey`) with `usersListKey` but is a DISTINCT shape on purpose:
// `fields` ('minimal' | 'full') sits between the root and the params so the
// privacy-tiered endpoints (/users/search vs /users) cache separately. The two
// never collide as entries (different lengths); don't try to unify them.
export const usersSearchKey = (fields: 'minimal' | 'full', params: UsersSearchKeyParams) =>
  [...usersBaseKey(), fields, params] as const;

/** Root of the slug-to-profile resolver family. Prefix-sweeps must use this
 *  (never a per-slug key): on a rename the SLUG ITSELF may be what changed. */
export const resolveProfileBaseKey = () => ['resolve-profile'] as const;

export const resolveProfileKey = (slug: string) => [...resolveProfileBaseKey(), slug] as const;
